import { StateManager } from '../../managers/StateManager';
import { ContextMenuHandlers } from './types';

/** A single entry in a context menu, matching the shape the ContextMenu component renders. */
export type ContextMenuItem = {
  label: string;
  action: () => void;
  dangerous?: boolean;
  closeOnClick?: boolean;
};

/** Dependencies the wire menu needs to read state and drive React modals/menus. */
export interface WireMenuDeps {
  stateManager: StateManager;
  contextMenuHandlers: ContextMenuHandlers;
}

/**
 * Opens a follow-up context menu letting the user pick which pin a wire end
 * terminates on (or clear the assignment). Falls back to an edit-pins shortcut
 * when the target connector has no pins yet.
 */
function showPinPicker(
  deps: WireMenuDeps,
  wireId: string,
  end: 'start' | 'end',
  connectorId: string,
  x: number,
  y: number
): void {
  const { stateManager, contextMenuHandlers } = deps;
  const connector = stateManager.getState().connectors.find(c => c.id === connectorId);
  const pins = connector?.pins ?? [];

  if (pins.length === 0) {
    contextMenuHandlers.showContextMenu(x, y, [
      {
        label: 'No pins defined - edit pins first',
        action: () => contextMenuHandlers.handleConnectorEditPins(connectorId)
      }
    ]);
    return;
  }

  contextMenuHandlers.showContextMenu(x, y, [
    {
      label: 'Unassigned',
      action: () => stateManager.assignWirePin(wireId, end, undefined)
    },
    ...pins.map(pin => ({
      label: `Pin ${pin.label}`,
      action: () => stateManager.assignWirePin(wireId, end, pin.id)
    }))
  ]);
}

/**
 * Builds the context-menu entries for a wire, shared by the canvas connector
 * wire rows and the wire table so both offer the same shortcuts. Returns an
 * empty list when the wire no longer exists.
 */
export function buildWireContextMenu(deps: WireMenuDeps, wireId: string, x: number, y: number): ContextMenuItem[] {
  const { stateManager, contextMenuHandlers } = deps;
  const wire = stateManager.getState().wires.find(w => w.id === wireId);
  if (!wire) {
    return [];
  }

  return [
    {
      label: 'Edit Wire',
      action: () => contextMenuHandlers.handleWireEdit(wireId)
    },
    {
      label: 'Assign Start Pin',
      action: () => showPinPicker(deps, wireId, 'start', wire.startConnectorId, x, y),
      closeOnClick: false
    },
    {
      label: 'Assign End Pin',
      action: () => showPinPicker(deps, wireId, 'end', wire.endConnectorId, x, y),
      closeOnClick: false
    },
    {
      label: 'Delete Wire',
      action: () => contextMenuHandlers.handleWireDelete(wireId),
      dangerous: true
    },
    {
      label: 'Highlight Wire Path',
      action: () => contextMenuHandlers.handleWireHighlight(wireId)
    }
  ];
}
